import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLead } from '../../services/notificationService';
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY_DIAL } from '../../constants/phone';
import { combineDialCodeAndNumber, sanitizeNationalNumber } from '../../utils/phone';

export default function Simulator({ titulo = "Simula tu pensión", tipo = "pensión" }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryDial, setCountryDial] = useState(DEFAULT_COUNTRY_DIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const countryOptions = useMemo(() => COUNTRY_OPTIONS, []);
  const currencyFormatter = useMemo(() => new Intl.NumberFormat('es-CO'), []);
  const handleAmountChange = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    setAmount(digits);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const normalizedPhone = combineDialCodeAndNumber(countryDial, phone);
    if (!normalizedPhone) {
      setError('Ingresa un número celular válido con el indicativo internacional.');
      return;
    }
    setError('');
    setSubmitting(true);
    const amountNumber = Number(amount);
    const contactChannel = `WhatsApp al ${normalizedPhone}`;
    const context = {
      preferredChannel: contactChannel,
      product: tipo,
      entryPoint: titulo,
      countryDialCode: countryDial,
      rawPhoneDigits: sanitizeNationalNumber(phone),
    };
    if (Number.isFinite(amountNumber) && amountNumber > 0) {
      context.amount = amountNumber;
    }
    const normalizedSource =
      typeof tipo === 'string' && tipo.length > 0
        ? `simulador-${tipo.toLowerCase().replace(/\s+/g, '-')}`
        : 'simulador-general';

    try {
      await createLead({
        name: trimmedName || undefined,
        phone: normalizedPhone,
        source: normalizedSource,
        context,
        notify: true,
      });
      navigate('/personas/simuladorPension');
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'No fue posible registrar tus datos. Intenta nuevamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center bg-[#E8F2F4] pt-6 px-4">

      <h2 className="text-[#0033a1] text-3xl sm:text-4xl lg:text-5xl text-center font-extrabold mb-8">{titulo}</h2>

      <div className="relative bg-[#E8F2F4] p-6 rounded-2xl shadow-xl w-full max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-[#E8F2F4] p-4 rounded-xl">
          <div className="space-y-1 md:col-span-3">
            <label className="text-lg font-bold text-[#0054c9] block mb-1" style={{ textShadow: '0 3px 4px rgba(0,0,0,0.3)' }}>¿Cuánto quieres invertir?</label>
            <div className="flex items-center bg-[#c1dbff] p-2 rounded-lg">
              <span className="text-lg">💲</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Ingresa el monto"
                className="w-full text-lg outline-none bg-transparent text-[#032c65] placeholder-[#032C65] ml-2"
                value={amount ? currencyFormatter.format(Number(amount)) : ''}
                onChange={handleAmountChange}
              />
            </div>
            <p className="text-sm font-bold text-[#4684d7] mt-1">Simula desde 500.000</p>
          </div>

          <div className="space-y-1 md:col-span-4">
            <label className="text-lg font-bold text-[#0054c9] block mb-1" style={{ textShadow: '0 3px 4px rgba(0,0,0,0.3)' }}>Tu nombre</label>
            <div className="flex items-center bg-[#c1dbff] p-2 rounded-lg gap-2">
              <span>👤</span>
              <input
                type="text"
                placeholder="Ingresa tu nombre"
                className="w-full text-lg outline-none bg-transparent text-[#032c65] placeholder-[#032C65] ml-2"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1 md:col-span-5">
            <label className="text-lg font-bold text-[#0054c9] block mb-1" style={{ textShadow: '0 3px 4px rgba(0,0,0,0.3)' }}>Tu número celular</label>
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1 flex items-center bg-[#c1dbff] p-2 rounded-lg gap-2">
                <span>🌐</span>
                <select
                  className="w-full text-lg outline-none bg-transparent text-[#032c65] appearance-none"
                  value={countryDial}
                  onChange={(event) => setCountryDial(event.target.value)}
                >
                  {countryOptions.map((option) => (
                    <option key={option.code} value={option.dial}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 flex items-center bg-[#c1dbff] p-2 rounded-lg gap-2">
                <span>📞</span>
                <input
                  type="tel"
                  inputMode="tel"
                  placeholder="321 321 3635"
                  className="w-full text-lg outline-none bg-transparent text-[#032c65] placeholder-[#032C65] ml-1"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-[#4684d7] font-semibold">
              Te contactaremos por WhatsApp al{' '}
              <span className="text-[#032c65]">
                {combineDialCodeAndNumber(countryDial, phone) || `${countryDial} ___`}
              </span>
            </p>
          </div>
        </div>

        <p className="text-xs text-[#4684d7] text-center mt-3 font-bold">
          Meu aliado no vende tus datos a terceros, y tú escoges el banco con el que abres tu CDT.{' '}
          <span className="text-[#0346a4] font-bold cursor-pointer">Conoce aquí cómo cuidamos tus datos</span>
        </p>

        {error ? <p className="text-sm text-red-600 text-center mt-2">{error}</p> : null}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#36a31a] text-white font-bold text-lg py-2 rounded-lg mt-4 hover:bg-[#55b03e] shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {submitting ? 'Enviando…' : '¡Simular ahora!'}
        </button>
      </div>

      <p className="text-[#0033a1] font-extrabold text-xl sm:text-2xl text-center px-4 sm:px-12 py-10 leading-tight">
        Nuestro simulador en MEU te permite ver los resultados en tiempo real de las tasas y bancos que ofrecen mejor rentabilidad.
      </p>
    </div>
  );
}
